import { useEffect, useRef } from "react";
import { getJSON } from "@/lib/api";
import { store } from "@/lib/store";
import { haversineKm, alertKey } from "@/lib/geo";
import { warn } from "@/lib/log";

const RADIUS_KM = 40;
const INTERVAL_MS = 3 * 60 * 1000;

// Watches a saved postcode for NEW severe alerts nearby and raises a browser
// notification. Works while a UK Ready tab is open (no push service worker).
export function useAreaWatch() {
  const timer = useRef(null);

  useEffect(() => {
    async function check() {
      const w = store.getWatch();
      if (!w || !w.enabled || w.lat == null) return;
      if (typeof Notification === "undefined" || Notification.permission !== "granted") return;

      let data;
      try { data = await getJSON("/live"); } catch { return; }

      const seen = new Set(store.getWatchSeen() || []);
      const near = (data.items || []).filter(
        (it) => it.severity === "severe" && it.lat != null && haversineKm(w.lat, w.lon, it.lat, it.lon) <= RADIUS_KM
      );

      // Prime on first run so we don't notify about pre-existing alerts.
      if (!w.primed) {
        near.forEach((it) => seen.add(alertKey(it)));
        store.saveWatchSeen([...seen]);
        store.saveWatch({ ...w, primed: true });
        return;
      }

      const fresh = near.filter((it) => !seen.has(alertKey(it)));
      fresh.slice(0, 3).forEach((it) => {
        try {
          new Notification(`UK Ready — severe alert near ${w.postcode || "your area"}`, {
            body: `${it.title} (${it.area})`,
            tag: alertKey(it),
          });
        } catch (e) { warn("Area watch check failed", e); }
      });
      near.forEach((it) => seen.add(alertKey(it)));
      store.saveWatchSeen([...seen].slice(-200));
    }

    const t0 = setTimeout(check, 6000);
    timer.current = setInterval(check, INTERVAL_MS);
    return () => { clearTimeout(t0); clearInterval(timer.current); };
  }, []);
}
